import { useEffect, useState } from 'react';
import { Form, Select, Row, Col, Switch, Typography, Divider, Tag, Alert } from 'antd';
import { useCallback } from 'react';
import { studioLookups } from '../../itemsApi';
import {
  ITEM_TYPES, INPUT_TYPES, MEASUREMENT_MODES, humaniseEnum
} from '../../studioConstants';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Step 2 — Classification & Usage.
//
// Picks the item's category, material type, item type, measurement
// mode, and flags. The Source Stock Item picker lets a finished /
// processed variant point at its base raw material — important for
// glass houses where one base material spawns multiple sellable SKUs.
// ─────────────────────────────────────────────────────────────────

export default function ClassificationUsageStep({ disabled, currentItemId }) {
  const [categories, setCategories]      = useState([]);
  const [materials, setMaterials]        = useState([]);
  const [sources, setSources]            = useState([]);
  const [sourceQ, setSourceQ]            = useState('');

  // Load categories + material-types once; the lookup endpoints are
  // small and cached at the backend layer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, mats] = await Promise.all([
          studioLookups.itemCategories({ q: '', isActive: true, limit: 500 }),
          studioLookups.materialTypes({ q: '', isActive: true, limit: 500 })
        ]);
        if (cancelled) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setMaterials(Array.isArray(mats) ? mats : []);
      } catch (_e) { /* swallow — page-level message handler covers errors */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Source-stock lookup is debounced via React's onSearch typing —
  // we issue requests on each keystroke (the endpoint is cheap and
  // protected by the standard apiUser rate-limit middleware).
  const loadSources = useCallback(async (q) => {
    try {
      const rows = await studioLookups.sourceStockItems({ q, limit: 50 });
      const filtered = Array.isArray(rows)
        ? rows.filter((r) => !currentItemId || r.id !== currentItemId)
        : [];
      setSources(filtered);
    } catch (_e) { /* swallow */ }
  }, [currentItemId]);

  useEffect(() => { loadSources(sourceQ); }, [sourceQ, loadSources]);

  return (
    <div>
      <h2 className="erp-studio-section-title">Classification & usage</h2>
      <p className="erp-studio-section-help">
        Tell the system what kind of item this is and how it behaves in transactions. The
        flags below decide which screens this item will appear on.
      </p>

      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <Form.Item label="Item type" name="itemType">
            <Select
              disabled={disabled}
              showSearch
              options={ITEM_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Input type" name="inputtype">
            <Select
              disabled={disabled}
              allowClear
              options={INPUT_TYPES.map((s) => ({ value: s, label: s }))}
              placeholder="MM (size) / OTH (other)"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Measurement mode" name="measurementMode">
            <Select
              disabled={disabled}
              allowClear
              options={MEASUREMENT_MODES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item label="Item category" name="itemCategoryId">
            <Select
              disabled={disabled}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Pick a category from Item Configuration"
              options={categories.map((c) => ({
                value: c.id,
                label: `${c.categoryCode || c.category_code} — ${c.categoryName || c.category_name}`
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Material type" name="materialTypeId">
            <Select
              disabled={disabled}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Pick a material type"
              options={materials.map((m) => ({
                value: m.id,
                label: `${m.materialTypeCode || m.material_type_code} — ${m.materialTypeName || m.material_type_name}`
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Source stock material</Text>
      <p className="erp-studio-section-help" style={{ marginTop: 4 }}>
        Use this when this item is a finished or processed variant made from another stock material.
        For example, the toughened-RG variant of 4MM Annealed Plain Clear Glass.
      </p>

      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item
            label="Source stock item"
            name="sourceStockItemId"
            extra="Leave blank if this item is itself a base stock."
          >
            <Select
              disabled={disabled}
              allowClear
              showSearch
              filterOption={false}
              onSearch={setSourceQ}
              placeholder="Search base stock items by code or name"
              options={sources.map((s) => ({
                value: s.id,
                label: `${s.itemCode || s.item_code} — ${s.itemName || s.item_name}`
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {currentItemId ? (
        <Alert
          type="info" showIcon
          style={{ marginBottom: 12, fontSize: 12 }}
          message="Self-link guard"
          description="An item cannot select itself as its source stock — the system blocks it automatically."
        />
      ) : null}

      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Usage flags</Text>
      <p className="erp-studio-section-help" style={{ marginTop: 4 }}>
        Tick the flags that apply — they drive which lists, forms and reports include this item.
      </p>

      <Tag color="default" style={{ marginBottom: 8 }}>
        At least one of <strong>Goods</strong> or <strong>Service</strong> must be on.
      </Tag>

      <div className="erp-studio-flags">
        <Form.Item label="Goods" name="isGoods" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Service" name="isService" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Stock item" name="isStockItem" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Purchasable" name="isPurchasable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Sellable" name="isSellable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Manufacturable" name="isManufacturable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Consumable" name="isConsumable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Jobwork item" name="isJobworkItem" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
      </div>
    </div>
  );
}
